import { useEffect, useState } from 'react'
import { useApiHealth } from '../store/apiHealth'

/**
 * Sticky banner shown at the top of the app when the API is blocked
 * — either rate-limited at the Cloudflare edge (HTTP 429, free-tier
 * 100k/day quota) or unreachable (TypeError "Failed to fetch").
 *
 * The cache is browser-local IndexedDB so cached pulls still work;
 * this banner just tells the operator why live polls are quiet.
 */
export function ApiHealthBanner() {
  const rateLimited = useApiHealth((s) => s.rateLimited)
  const networkDown = useApiHealth((s) => s.networkDown)
  const resumesAt = useApiHealth((s) => s.resumesAt)
  const lastError = useApiHealth((s) => s.lastError)
  const clear = useApiHealth((s) => s.clear)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (!rateLimited) return
    const id = setInterval(() => setNow(Date.now()), 30_000)
    return () => clearInterval(id)
  }, [rateLimited])

  if (!rateLimited && !networkDown) return null

  let countdown = ''
  if (resumesAt) {
    const ms = Math.max(0, Date.parse(resumesAt) - now)
    const h = Math.floor(ms / 3_600_000)
    const m = Math.floor((ms % 3_600_000) / 60_000)
    countdown = `${h}h ${m}m`
  }

  return (
    <div className="border-b border-amber-400/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2">
        <span className="text-base">⚠️</span>
        {rateLimited ? (
          <span className="flex-1">
            <strong>API daily quota reached</strong> — Cloudflare Workers free
            tier caps at 100k requests/day. Live polls paused; cached views
            (Home → follows / blocks / saved, gallery from cached posts, horny
            meter) still work.
            {countdown ? (
              <>
                {' '}
                Resumes in <strong>{countdown}</strong> (UTC midnight).
              </>
            ) : null}{' '}
            Bumping to the Workers Paid plan ($5/mo, 10M req) at{' '}
            <a
              href="https://dash.cloudflare.com/?to=/:account/workers/plans"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              dash.cloudflare.com
            </a>{' '}
            removes this cap.
          </span>
        ) : (
          <span className="flex-1">
            <strong>API unreachable</strong> — {lastError ?? 'fetch failed'}.
            The worker may be redeploying or your network is offline; cached
            views still load.
          </span>
        )}
        <button
          type="button"
          onClick={clear}
          className="rounded-full border border-white/20 bg-white/10 px-2 py-0.5 text-[11px] text-white hover:bg-white/20"
          title="dismiss + retry next call"
        >
          retry
        </button>
      </div>
    </div>
  )
}
